import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { parseDollarsToCents } from '@crave/shared';
import { ApiError } from '@crave/api-client';
import { Button, Card, Field, FormGrid, SelectField } from '@crave/ui';
import { api } from '../api.ts';
import { Notice, Section, Table, money } from '../components/ui.tsx';

interface Rules {
  is_enabled: boolean;
  earn_rate_bps: number | null;
  min_order_cents: number;
  credit_expiry_days: number | null;
  birthday_enabled: boolean;
  birthday_menu_item_id: string | null;
}
interface RulesResponse {
  rules: Rules;
  menu_items: { id: string; name: string }[];
}
interface LoyaltyReport {
  by_month: {
    month: string;
    earned_cents: number;
    redeemed_cents: number;
    expired_cents: number;
    adjusted_cents: number;
  }[];
  totals: {
    outstanding_cents: number;
    accounts_with_balance: number;
    accounts: number;
    with_birthday: number;
    birthdays_redeemed: number;
    birthdays_open: number;
    birthday_cost_cents: number;
  };
  top_customers: {
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    balance_cents: number;
    lifetime_earned_cents: number;
    orders: number;
  }[];
}

interface RuleErrors {
  pct?: string;
  min?: string;
  expiry?: string;
}

/** "5", "5.0", "5%", "2,5" all parse; anything else is null. */
function parsePercent(input: string): number | null {
  const t = input.replace(/\s+/g, '').replace(/%$/, '').replace(',', '.');
  if (!/^(\d+(\.\d{1,2})?|\.\d{1,2})$/.test(t)) return null;
  return Number(t);
}
/** Dollars with optional thousands commas, like "1,200.50". */
const parseMoney = (input: string) => parseDollarsToCents(input.replace(/,(?=\d{3}(\D|$))/g, ''));

const MIN_EXPIRY_DAYS = 30;
const MAX_EXPIRY_DAYS = 3650;

export function Loyalty() {
  const qc = useQueryClient();
  const rules = useQuery({
    queryKey: ['loyalty-rules'],
    queryFn: async () => (await api.get<RulesResponse>('/admin/loyalty/rules')).data,
  });
  const report = useQuery({
    queryKey: ['loyalty-report'],
    queryFn: async () => (await api.get<LoyaltyReport>('/admin/reports/loyalty')).data,
  });
  const [form, setForm] = useState({
    enabled: false,
    pct: '',
    min: '',
    expiry: '',
    birthday: false,
    item: '',
  });
  useEffect(() => {
    const r = rules.data?.rules;
    if (!r) return;
    setForm({
      enabled: r.is_enabled,
      pct: r.earn_rate_bps === null ? '' : String(r.earn_rate_bps / 100),
      min: r.min_order_cents ? String(r.min_order_cents / 100) : '',
      expiry: r.credit_expiry_days === null ? '' : String(r.credit_expiry_days),
      birthday: r.birthday_enabled,
      item: r.birthday_menu_item_id ?? '',
    });
  }, [rules.data]);
  const [errors, setErrors] = useState<RuleErrors>({});
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string; mfa?: boolean } | null>(
    null,
  );
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.put('/admin/loyalty/rules', body),
    onSuccess: () => {
      setMsg({ kind: 'ok', text: 'Saved. Changes apply to the next completed order.' });
      void qc.invalidateQueries({ queryKey: ['loyalty-rules'] });
    },
    onError: (e) =>
      setMsg(
        e instanceof ApiError && e.code === 'MFA_REQUIRED'
          ? {
              kind: 'error',
              text: 'Two-factor sign-in is required to change Crave Credit rules.',
              mfa: true,
            }
          : { kind: 'error', text: e instanceof ApiError ? e.message : 'Could not save.' },
      ),
  });

  const t = report.data?.totals;
  return (
    <Section title="Crave Credit & birthday pizza">
      {msg && (
        <Notice kind={msg.kind}>
          {msg.text}
          {msg.mfa && (
            <>
              {' '}
              <Link to="/settings/security">Open Security</Link> to verify this session.
            </>
          )}
        </Notice>
      )}
      <Card>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setMsg(null);
            const next: RuleErrors = {};

            let pct: number | null = null;
            if (form.pct.trim() !== '') {
              pct = parsePercent(form.pct);
              if (pct === null) next.pct = 'Enter a percent like 5 or 2.5.';
              else if (pct > 50) next.pct = 'The earn rate must be 50% or less.';
            }
            if (form.enabled && !next.pct && (pct === null || pct === 0))
              next.pct = 'Set an earn rate before turning on Crave Credit.';

            let minCents = 0;
            if (form.min.trim() !== '') {
              const c = parseMoney(form.min);
              if (c === null) next.min = 'Enter dollars like 15 or 15.50.';
              else if (c > 100_000) next.min = 'The minimum order must be $1,000 or less.';
              else minCents = c;
            }

            let expiry: number | null = null;
            if (form.expiry.trim() !== '') {
              const days = /^\d+$/.test(form.expiry.trim()) ? Number(form.expiry.trim()) : null;
              if (days === null) next.expiry = 'Enter whole days, like 365.';
              else if (days < MIN_EXPIRY_DAYS)
                next.expiry = `Credit must last at least ${MIN_EXPIRY_DAYS} days. Leave blank for never.`;
              else if (days > MAX_EXPIRY_DAYS)
                next.expiry = `Enter ${MAX_EXPIRY_DAYS} days or fewer. Leave blank for never.`;
              else expiry = days;
            }

            setErrors(next);
            if (Object.keys(next).length > 0) return;
            save.mutate({
              is_enabled: form.enabled,
              earn_rate_bps: pct === null ? null : Math.round(pct * 100),
              min_order_cents: minCents,
              credit_expiry_days: expiry,
              birthday_enabled: form.birthday,
              birthday_menu_item_id: form.item || null,
            });
          }}
          style={{ display: 'grid', gap: 8 }}
        >
          <Check
            checked={form.enabled}
            onChange={(checked) => setForm({ ...form, enabled: checked })}
          >
            Customers earn Crave Credit
          </Check>
          <FormGrid min={220}>
            <Field
              label="Earn rate (% of food paid)"
              inputMode="decimal"
              value={form.pct}
              onChange={(e) => setForm({ ...form, pct: e.target.value })}
              hint="Never on tax or tip."
              error={errors.pct}
            />
            <Field
              label="Minimum order ($)"
              inputMode="decimal"
              value={form.min}
              onChange={(e) => setForm({ ...form, min: e.target.value })}
              hint="Blank means any order earns."
              error={errors.min}
            />
            <Field
              label="Credit expires after (days)"
              inputMode="numeric"
              value={form.expiry}
              onChange={(e) => setForm({ ...form, expiry: e.target.value })}
              hint={`At least ${MIN_EXPIRY_DAYS} days. Blank means it never expires.`}
              error={errors.expiry}
            />
          </FormGrid>
          <Check
            checked={form.birthday}
            onChange={(checked) => setForm({ ...form, birthday: checked })}
          >
            Free pizza in the customer's birthday month
          </Check>
          {form.birthday && (
            <FormGrid min={220}>
              <SelectField
                wide
                style={{ maxWidth: 460 }}
                label="Which pizza"
                value={form.item}
                onChange={(e) => setForm({ ...form, item: e.target.value })}
                placeholder="Any pizza (the cheapest one in the order is free)"
                options={(rules.data?.menu_items ?? []).map((m) => [m.id, m.name] as const)}
              />
            </FormGrid>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 4 }}>
            <Button type="submit" disabled={save.isPending || !rules.data}>
              {save.isPending ? 'Saving…' : 'Save rules'}
            </Button>
          </div>
        </form>
      </Card>

      {t && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 12,
          }}
        >
          <Stat label="Credit outstanding" value={money(t.outstanding_cents)} />
          <Stat label="Accounts" value={String(t.accounts)} />
          <Stat label="With a balance" value={String(t.accounts_with_balance)} />
          <Stat label="Birthdays saved" value={String(t.with_birthday)} />
          <Stat label="Birthday pizzas used" value={String(t.birthdays_redeemed)} />
          <Stat label="Birthday pizza cost" value={money(t.birthday_cost_cents)} />
        </div>
      )}
      <h3 style={{ margin: 0 }}>By month</h3>
      <Table
        head={['Month', 'Earned', 'Used', 'Expired', 'Adjusted']}
        rows={(report.data?.by_month ?? []).map((m) => [
          m.month,
          money(m.earned_cents),
          money(m.redeemed_cents),
          money(m.expired_cents),
          money(m.adjusted_cents),
        ])}
        empty="No credit activity yet."
      />
      <h3 style={{ margin: 0 }}>Top customers</h3>
      <Table
        head={['Customer', 'Orders', 'Earned all time', 'Balance']}
        rows={(report.data?.top_customers ?? []).map((c) => [
          <Link to={`/customers/${c.id}`}>
            {[c.first_name, c.last_name].filter(Boolean).join(' ') || c.email}
          </Link>,
          String(c.orders),
          money(c.lifetime_earned_cents),
          money(c.balance_cents),
        ])}
        empty="Nobody has earned credit yet."
      />
    </Section>
  );
}

/** A checkbox row: box and text centred on one line, text wraps beside the box. */
function Check({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
}) {
  return (
    <label
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'center',
        minHeight: 'var(--touch)',
        fontWeight: 600,
        lineHeight: 1.3,
        cursor: 'pointer',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: 20, height: 20, margin: 0, flex: 'none' }}
      />
      <span>{children}</span>
    </label>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800 }}>{value}</div>
    </Card>
  );
}
